/**
 * Trip category: the app's guess, the correction, and the editable list behind it.
 *
 * Correcting a guess is the only training signal this part of the system gets (PROJECT.md), so the
 * guess is shown with its confidence and the correction is one tap away rather than buried in a
 * settings screen. Destructive edits confirm inline; a modal over a shopping trip is not on.
 */
import { useState } from "react";
import { api, errorMessage } from "../api.ts";
import { percent } from "../format.ts";
import { navigate } from "../hooks.ts";
import { useModel } from "../model.tsx";
import { Icon } from "../components/icons.tsx";
import { Badge, Meter, Note } from "../components/ui.tsx";

type RowMode = { id: number; mode: "rename" | "merge" | "delete"; target: number | null };

export function Category() {
	const model = useModel();
	const { basket, rec, categories } = model;
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [row, setRow] = useState<RowMode | null>(null);
	const [draft, setDraft] = useState("");
	const [fresh, setFresh] = useState("");

	const run = async (op: () => Promise<unknown>): Promise<void> => {
		setBusy(true);
		setError(null);
		try {
			await op();
			model.setCategories(await api.categories());
			setRow(null);
		} catch (err) {
			setError(errorMessage(err));
		} finally {
			setBusy(false);
		}
	};

	const chosen = basket?.categoryId ?? null;
	const active = categories.filter((category) => !category.archived);
	const archived = categories.filter((category) => category.archived);
	const guessName = rec?.categoryName ?? null;
	const guessing = rec !== null && rec.categorySource === "INFERRED" && rec.categoryId !== null;

	return (
		<>
			<main className="screen stack">
				<section className="panel">
					<h1 className="panel-title">This trip</h1>
					{chosen !== null ? (
						<div className="trip-state trip-chosen">
							<span className="trip-label">
								<Icon name="check" /> You chose
							</span>
							<strong>{categories.find((category) => category.id === chosen)?.name ?? `#${chosen}`}</strong>
							<p>Manual choice wins over any guess, and every card scoped to this category is in play.</p>
							<button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void model.chooseCategory(null)}>
								Clear and let the app guess
							</button>
						</div>
					) : guessing ? (
						<div className="trip-state trip-guess">
							<span className="trip-label">
								<Icon name="info" /> The app guesses
							</span>
							<strong>{guessName}</strong>
							{rec.categoryConfidence !== null && (
								<>
									<Meter value={rec.categoryConfidence} />
									<span className="trip-conf">{percent(rec.categoryConfidence)} confident</span>
								</>
							)}
							<p>
								A guess is not a label. Confirm it and it becomes training data; correct it and the next basket
								like this one guesses better.
							</p>
							<button
								type="button"
								className="btn btn-primary"
								disabled={busy || rec.categoryId === null}
								onClick={() => rec.categoryId !== null && void model.chooseCategory(rec.categoryId)}
							>
								<Icon name="check" /> Confirm {guessName}
							</button>
						</div>
					) : (
						<div className="trip-state trip-none">
							<span className="trip-label">
								<Icon name="alert" /> No category
							</span>
							<p>
								{basket === null || basket.lines.length === 0
									? "Scan a few items and the app will have something to go on."
									: "The basket does not look enough like any labelled trip to call it, so it abstained instead of guessing. Pick one below; every card is otherwise in play, which risks polluting a specialised profile."}
							</p>
						</div>
					)}
				</section>

				{error !== null && <Note tone="danger">{error}</Note>}

				<section className="panel">
					<h2 className="panel-title">Pick for this trip</h2>
					<div className="picker">
						{active.map((category) => (
							<button
								key={category.id}
								type="button"
								className={category.id === chosen ? "pick pick-on" : "pick"}
								disabled={busy}
								onClick={() => void model.chooseCategory(category.id)}
							>
								<span className="pick-name">{category.name}</span>
								<span className="pick-meta">
									{category.tripCount} {category.tripCount === 1 ? "trip" : "trips"}
									{category.id === rec?.categoryId && rec.categorySource === "INFERRED" ? " \u00b7 guess" : ""}
								</span>
							</button>
						))}
					</div>
				</section>

				<section className="panel">
					<h2 className="panel-title">Categories</h2>
					<form
						className="manual"
						onSubmit={(event) => {
							event.preventDefault();
							const name = fresh.trim();
							if (name === "") return;
							setFresh("");
							void run(() => api.createCategory(name));
						}}
					>
						<Icon name="plus" />
						<input
							value={fresh}
							onChange={(event) => setFresh(event.target.value)}
							placeholder="New category"
							aria-label="New category name"
							autoComplete="off"
						/>
						<button type="submit" className="btn btn-quiet" disabled={busy || fresh.trim() === ""}>
							Create
						</button>
					</form>

					<ul className="catlist">
						{[...active, ...archived].map((category) => {
							const editing = row !== null && row.id === category.id ? row : null;
							return (
								<li key={category.id} className={category.archived ? "cat cat-archived" : "cat"}>
									<span className="cat-head">
										<span className="cat-name">{category.name}</span>
										{category.isDefault && <Badge>default</Badge>}
										{category.archived && <Badge tone="warn">archived</Badge>}
										<span className="cat-trips">
											{category.tripCount} {category.tripCount === 1 ? "trip" : "trips"}
										</span>
									</span>

									{editing === null ? (
										<span className="cat-actions">
											<button
												type="button"
												className="btn btn-quiet"
												disabled={busy}
												onClick={() => {
													setDraft(category.name);
													setRow({ id: category.id, mode: "rename", target: null });
												}}
											>
												Rename
											</button>
											<button
												type="button"
												className="btn btn-quiet"
												disabled={busy || categories.length < 2}
												onClick={() => setRow({ id: category.id, mode: "merge", target: null })}
											>
												<Icon name="merge" /> Merge
											</button>
											<button
												type="button"
												className="btn btn-quiet"
												disabled={busy}
												onClick={() => void run(() => api.archiveCategory(category.id, !category.archived))}
											>
												{category.archived ? "Restore" : "Archive"}
											</button>
											<button
												type="button"
												className="btn btn-quiet btn-danger"
												aria-label={`Delete ${category.name}`}
												title={`Delete ${category.name}`}
												disabled={busy}
												onClick={() => setRow({ id: category.id, mode: "delete", target: null })}
											>
												<Icon name="trash" />
											</button>
										</span>
									) : editing.mode === "rename" ? (
										<form
											className="cat-edit"
											onSubmit={(event) => {
												event.preventDefault();
												const name = draft.trim();
												if (name === "" || name === category.name) {
													setRow(null);
													return;
												}
												void run(() => api.renameCategory(category.id, name));
											}}
										>
											<input
												value={draft}
												onChange={(event) => setDraft(event.target.value)}
												aria-label={`Rename ${category.name}`}
												autoFocus
											/>
											<button type="submit" className="btn btn-primary" disabled={busy}>
												Save
											</button>
											<button type="button" className="btn btn-quiet" onClick={() => setRow(null)}>
												Cancel
											</button>
										</form>
									) : editing.mode === "merge" ? (
										<div className="cat-edit cat-merge">
											{editing.target === null ? (
												<>
													<span className="cat-hint">Merge {category.name} into&hellip;</span>
													<span className="chips">
														{categories
															.filter((other) => other.id !== category.id)
															.map((other) => (
																<button
																	key={other.id}
																	type="button"
																	className="chip"
																	disabled={busy}
																	onClick={() => setRow({ id: category.id, mode: "merge", target: other.id })}
																>
																	{other.name}
																</button>
															))}
													</span>
													<button type="button" className="btn btn-quiet" onClick={() => setRow(null)}>
														Cancel
													</button>
												</>
											) : (
												<>
													<span className="cat-hint cat-warn">
														{category.tripCount} {category.tripCount === 1 ? "trip" : "trips"} and every card link move
														to {categories.find((other) => other.id === editing.target)?.name}. {category.name}{" "}
														disappears.
													</span>
													<button
														type="button"
														className="btn btn-danger"
														disabled={busy}
														onClick={() => {
															const target = editing.target;
															if (target !== null) void run(() => api.mergeCategory(category.id, target));
														}}
													>
														Merge
													</button>
													<button type="button" className="btn btn-quiet" onClick={() => setRow(null)}>
														Cancel
													</button>
												</>
											)}
										</div>
									) : (
										<div className="cat-edit">
											<span className="cat-hint cat-warn">
												Delete {category.name}? Its {category.tripCount}{" "}
												{category.tripCount === 1 ? "trip" : "trips"} lose their label. Merge instead if you want to
												keep the history.
											</span>
											<button
												type="button"
												className="btn btn-danger"
												disabled={busy}
												onClick={() => void run(() => api.deleteCategory(category.id))}
											>
												Delete
											</button>
											<button type="button" className="btn btn-quiet" onClick={() => setRow(null)}>
												Cancel
											</button>
										</div>
									)}
								</li>
							);
						})}
					</ul>
				</section>
			</main>

			<div className="actionbar">
				<button type="button" className="btn btn-ghost btn-lg" onClick={() => navigate("scan")}>
					Keep scanning
				</button>
				<button
					type="button"
					className="btn btn-primary btn-lg"
					disabled={basket === null || basket.lines.length === 0}
					onClick={() => navigate("result")}
				>
					Recommendation
					<Icon name="chevron" />
				</button>
			</div>
		</>
	);
}
